import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, RefreshCw, Cable, CheckCircle2, AlertTriangle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBridgePanel() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bridge/status`, { headers });
      setStatus(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reach bridge'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/bridge/sync`, {}, { headers });
      setLastSync(r.data);
      if (r.data.pushed) toast.success(`Pushed ${r.data.counts?.events} events + ${r.data.counts?.venues} venues to EventGoers`);
      else toast.error('Sync attempted but EventGoers did not accept — see details below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setSyncing(false);
  };

  const reachable = status?.configured && status?.reachable;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="admin-bridge-panel">
      <div className="max-w-3xl mx-auto px-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
        </Button>
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2 font-['Space_Grotesk']">
            <Cable className="w-7 h-7 text-cyan-400" /> EventGoers Bridge
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Outbound feed of every published event + active venue to EventGoers.com.</p>
        </div>

        <Card className="mb-4">
          <CardContent className="p-5">
            {loading ? (
              <div className="text-center py-4"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    {reachable ? (
                      <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> CONNECTED</Badge>
                    ) : status?.configured ? (
                      <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/30"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> WAITING FOR EVENTGOERS</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-rose-500/15 text-rose-300 border-rose-500/30">NOT CONFIGURED</Badge>
                    )}
                    {status?.http_status && <span className="text-xs text-muted-foreground font-mono">HTTP {status.http_status}</span>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={checkStatus} data-testid="bridge-recheck"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-check</Button>
                </div>
                {!status?.configured && <p className="text-sm text-muted-foreground">Set <code className="text-xs">EVENTGOERS_BRIDGE_KEY</code> in <code className="text-xs">backend/.env</code> to enable.</p>}
                {status?.configured && !status?.reachable && (
                  <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-sm">
                    <p className="font-semibold mb-1">Bridge is configured on Eventism but EventGoers' endpoints aren't responding yet.</p>
                    <p className="text-xs text-muted-foreground">EventGoers needs to redeploy with the bridge endpoints enabled. Once they do, this panel will flip to CONNECTED automatically. New events created on Eventism are already attempting auto-push (and silently retrying on the next save).</p>
                  </div>
                )}
                {status?.remote && (
                  <details className="text-xs">
                    <summary className="text-muted-foreground cursor-pointer">Raw remote response</summary>
                    <pre className="mt-2 bg-slate-900/50 border border-border rounded p-2 overflow-x-auto">{JSON.stringify(status.remote, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><Send className="w-4 h-4 text-indigo-400" /> Bulk sync</h3>
            <p className="text-sm text-muted-foreground mb-3">Push every published event + active venue in one batch (deduped on EventGoers' side by source_id).</p>
            <Button onClick={runSync} disabled={syncing || !status?.configured} className="bg-gradient-to-r from-indigo-600 to-cyan-600" data-testid="bridge-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Sync everything now
            </Button>
            {lastSync && (
              <div className="mt-3 rounded-md bg-slate-900/50 border border-border p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Pushed</span><span className="font-mono">{lastSync.pushed ? '✅' : '❌'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Events queued</span><span className="font-mono">{lastSync.counts?.events}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Venues queued</span><span className="font-mono">{lastSync.counts?.venues}</span></div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 text-xs text-muted-foreground space-y-2">
            <p><strong className="text-foreground">How auto-push works:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Every time you create or update a hosted event (and it's published), Eventism fires a non-blocking POST to EventGoers <code>/ingest/event</code>.</li>
              <li>Same for venues — create or update an active venue → push to <code>/ingest/venue</code>.</li>
              <li>If EventGoers is offline or returns an error, the push is silently dropped (logged on the server). Re-sync via the button above to backfill.</li>
              <li>EventGoers dedupes by <code>source_id</code> (your event_id / venue_id) — re-pushing the same row updates the existing record there.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
